import { List } from "immutable";

import {
  ITEM_CARD_IDENTIFIER_MARGIN_X,
  ITEM_CARD_IDENTIFIER_ROW_MARGIN_Y,
  ITEM_CARD_IMAGE_HEIGHT,
  ITEM_CARD_IMAGE_MARGIN_TOP,
  ITEM_CARD_IMAGE_WIDTH,
  ITEM_CARD_INLINE_AVATAR_MARGIN_Y,
  ITEM_CARD_INLINE_AVATAR_SIZE,
  ITEM_CARD_MARGIN_Y,
  ITEM_CARD_MAX_INLINE_IDENTIFIERS
} from "../../constants";
import { METRICS_PREFIX } from "../../constants/itemFields";
import Dimensions, { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import { getItemCardNestedTextWidth } from "../../modules/display/displayItems/reducers/updateItemTextSizes";
import { immutableRecords } from "../../types";
import { SizeRecord } from "../../types/immutableRecords/helper/SizeRecord";
import { calcIdentifierSize, calcSingleLineTextSize, calcTextSize, calcWordWidth } from "../../utils/calcTextSize";

const { MAX_ITEM_TEXT_WIDTH } = Dimensions;

/**
 * Update the durationTextSize of an item display object.
 * Limit the units of duration to 2
 * and recalculate the width when necessary
 * @param {TextSizeRecord} origTextSize
 * @param {number} fontSize
 * @param {Immutable.Map} widthMap
 */
export const getDurationTextSize = (origTextSize, fontSize, widthMap) => {
  const text = origTextSize.text;
  if (text) {
    const splitter = ", ";
    const textUnits = text.split(splitter);
    if (textUnits.length > 2) {
      const newText = textUnits.slice(0, 2).join(splitter);
      const newWidth = calcWordWidth(newText, fontSize, widthMap);
      return origTextSize.withMutations(rm => {
        rm.set("text", newText);
        rm.setIn(["size", "width"], newWidth);
      });
    }
  }
  return origTextSize;
};

const getSizeSum = sizes =>
  sizes.reduce(
    (sum, s) => {
      sum.width += s.width;
      sum.height = Math.max(sum.height, s.height);
      return sum;
    },
    { width: 0, height: 0 }
  );

// Avatars, blocks, blocked by
const getRemainerSize = (remain, fontSize, widthMap) => {
  if (remain > 0) {
    const scaledPadding = scalePixelsToFontSize(Dimensions.ITEM_CARD_AVATAR_REMAINER.PADDING_LEFT, fontSize);
    const scaledMargin = scalePixelsToFontSize(ITEM_CARD_IDENTIFIER_MARGIN_X, fontSize);
    const scaledFontSize = scalePixelsToFontSize(Dimensions.ITEM_CARD_AVATAR_REMAINER.FONT_SIZE, fontSize);
    const scaledHeight = scalePixelsToFontSize(Dimensions.ITEM_CARD_AVATAR_REMAINER.LINE_HEIGHT, fontSize);
    const width =
      calcSingleLineTextSize(`... + ${remain} more`, scaledFontSize, widthMap).size.width +
      scaledPadding +
      scaledMargin;
    return { width, height: scaledHeight };
  }
  return SizeRecord();
};

const getIdentifierIconSize = fontSize => {
  const width = scalePixelsToFontSize(Dimensions.ITEM_CARD_BLOCK_ICON_SIZE + ITEM_CARD_IDENTIFIER_MARGIN_X, fontSize);
  const height = scalePixelsToFontSize(Dimensions.ITEM_CARD_BLOCK_ICON_SIZE, fontSize);
  return { width, height };
};

const getIdentifiersSize = (identifiers, maxIds, fontSize, widthMap) => {
  if (identifiers.size > 0) {
    const exceedsMax = identifiers.size >= maxIds;
    const count = exceedsMax ? maxIds : identifiers.size;
    const identifiersSizes = identifiers.slice(0, count).map(i => {
      const size = calcIdentifierSize(i.label, i.isAvatar, fontSize, widthMap);
      return size;
    });

    const identifierSizeSum = getSizeSum(identifiersSizes);
    const remainderSize = getRemainerSize(identifiers.size - maxIds, fontSize, widthMap);

    const iconSize = getIdentifierIconSize(fontSize);

    return getSizeSum([identifierSizeSum, remainderSize, iconSize]);
  }
  return SizeRecord();
};

const getBlockingIdentifierSize = (identifiers, fontSize, widthMap) =>
  getIdentifiersSize(identifiers, ITEM_CARD_MAX_INLINE_IDENTIFIERS, fontSize, widthMap);

const getRelatedIdentifierSize = (identifiers, fontSize, widthMap, maxWidth) => {
  let result = SizeRecord();
  if (identifiers.size > 0) {
    const identifiersSizes = identifiers.map(i => {
      const size = calcIdentifierSize(i.label, i.isAvatar, fontSize, widthMap);
      return size;
    });
    const iconSize = getIdentifierIconSize(fontSize);
    let rowWidth = iconSize.width;
    let width = rowWidth;
    let height = iconSize.height;
    identifiersSizes.forEach(s => {
      if (rowWidth + s.width > maxWidth) {
        width = Math.max(width, rowWidth, s.width);
        rowWidth = s.width;
        height = height + s.height;
      } else {
        rowWidth += s.width;
        width = Math.max(rowWidth, width);
        height = Math.max(s.height, height);
      }
    });
    result = SizeRecord({ width, height });
  }
  return result;
};

const getRelatedIdentifierSizes = (item, showRelationshipTypes, fontSize, widthMap, maxWidth) => {
  const { relatedItemsByType } = item;
  let sizesList = List();
  if (showRelationshipTypes && showRelationshipTypes.size > 0) {
    sizesList = relatedItemsByType
      .filter(listOfType => showRelationshipTypes.includes(listOfType.typeId))
      .map(listOfType => getRelatedIdentifierSize(listOfType.idDisplayList, fontSize, widthMap, maxWidth));
  }
  return sizesList;
};

const getMaxIdentifierHeight = fontSize =>
  scalePixelsToFontSize(ITEM_CARD_INLINE_AVATAR_SIZE + 2 * ITEM_CARD_INLINE_AVATAR_MARGIN_Y, fontSize);

const getInlineWrappingSize = (sizes, maxWidth, fontSize) => {
  let rowWidth = 0;
  let rowHeight = 0;
  let resultWidth = 0;
  let resultHeight = 0;
  const firstNonZeroIndex = sizes.findIndex(s => s.width > 0 && s.height > 0);
  if (firstNonZeroIndex >= 0) {
    const maxLineHeight = getMaxIdentifierHeight(fontSize);
    const firstSize = sizes[firstNonZeroIndex];
    rowWidth = firstSize.width;
    rowHeight = firstSize.height;
    resultWidth = firstSize.width;
    resultHeight = firstSize.height;
    sizes.slice(firstNonZeroIndex + 1).forEach(s => {
      const { width, height } = s;
      if (width > 0 && height > 0) {
        // Total row width larger than the max width or the row height larger than the max line height
        if (rowWidth + width > maxWidth || rowHeight > maxLineHeight) {
          rowWidth = width;
          rowHeight = height;
          resultHeight += height;
        } else {
          rowWidth += width;
          const heightDiff = height - rowHeight;
          rowHeight = heightDiff > 0 ? height : rowHeight;
          resultHeight = heightDiff > 0 ? resultHeight + heightDiff : resultHeight;
        }
        resultWidth = Math.max(resultWidth, rowWidth);
      }
    });
  }
  resultWidth = Math.min(resultWidth, maxWidth);
  // The identifier row has margins
  const scaledLineMarginY = scalePixelsToFontSize(ITEM_CARD_IDENTIFIER_ROW_MARGIN_Y, fontSize);
  resultHeight = resultHeight + (resultHeight > 0 ? scaledLineMarginY * 2 : 0);
  return { width: resultWidth, height: resultHeight };
};

const getRelatedIdentifersLineSize = (item, settings, fontSize, widthMap, maxWidth = MAX_ITEM_TEXT_WIDTH) => {
  const idSize = settings.showIdentifier ? item.identifierSize : SizeRecord();
  const relatedItemsSizes = getRelatedIdentifierSizes(
    item,
    settings.showRelationshipTypes,
    fontSize,
    widthMap,
    maxWidth
  );

  const blockingSize = settings.showBlocks
    ? getBlockingIdentifierSize(item.blockingIdentifiers, fontSize, widthMap)
    : SizeRecord();
  const blockedSize = settings.showBlockedBy
    ? getBlockingIdentifierSize(item.blockedByIdentifiers, fontSize, widthMap)
    : SizeRecord();

  const sizes = [idSize].concat(relatedItemsSizes.toArray()).concat([blockingSize, blockedSize]);

  return getInlineWrappingSize(sizes, maxWidth, fontSize);
};

export const calcItemCardDateLineRelated = (
  showDates,
  showDuration,
  dateTextSize,
  durationTextSize,
  fontSize,
  widthMap,
  maxWidth
) => {
  const dateTextMetrics = calcTextSize(dateTextSize.text, fontSize, widthMap, maxWidth);
  const dateLastWidth = calcSingleLineTextSize(dateTextMetrics.lines.last(), fontSize, widthMap).size.width;

  // The duration text size has been adjusted here
  const adjustedDurationTextSize = getDurationTextSize(durationTextSize, fontSize, widthMap);

  const dateWidth = dateTextMetrics.size.width;
  const dateHeight = dateTextMetrics.size.height;

  const { width: durationWidth, height: durationHeight } = adjustedDurationTextSize.size;

  let width = 0;
  let height = 0;
  let isDateDurationSplitLine = false;

  if (showDates && showDuration) {
    // Normally would show "date (duration)"
    // date width + duration width + " ()"
    const lastLineWidth = dateLastWidth + durationWidth + calcWordWidth(" ()", fontSize, widthMap);
    height = dateHeight;
    // Should put duration to a new line if the total width exceeds max width
    if (lastLineWidth > maxWidth) {
      width = Math.max(dateWidth, dateLastWidth, durationWidth);
      height = dateHeight + durationHeight;
      isDateDurationSplitLine = true;
    } else {
      width = Math.max(dateWidth, lastLineWidth);
    }
  } else {
    width = (showDates ? dateWidth : 0) + (showDuration ? durationWidth : 0);
    height = showDates ? dateHeight : showDuration ? durationHeight : 0;
  }

  return { width, height, dateTextMetrics, durationTextSize: adjustedDurationTextSize, isDateDurationSplitLine };
};

const calcItemCardSize = (item, settings, fontSize, widthMap, titleMetricsKey) => {
  const titleMetrics = titleMetricsKey ? item.get(titleMetricsKey).size : item.titleMetrics.size;

  const narrativeTextSize = item.narrativeTextSize.size;
  const summaryMetrics = item.summaryMetrics.size;

  const SCALED_IMAGE_WIDTH = scalePixelsToFontSize(ITEM_CARD_IMAGE_WIDTH, fontSize);
  const SCALED_IMAGE_HEIGHT = scalePixelsToFontSize(ITEM_CARD_IMAGE_HEIGHT, fontSize);
  const SCALED_IMAGE_MARGIN_TOP = scalePixelsToFontSize(ITEM_CARD_IMAGE_MARGIN_TOP, fontSize);

  const SCALED_MAX_ITEM_TEXT_WIDTH = scalePixelsToFontSize(MAX_ITEM_TEXT_WIDTH, fontSize);

  const { width: identifiersLineWidth, height: identifiersLineHeight } = getRelatedIdentifersLineSize(
    item,
    settings,
    fontSize,
    widthMap,
    SCALED_MAX_ITEM_TEXT_WIDTH
  );

  // This width is determined by the maximum width of the title, narrative, summary, avatars, image
  // Note: maximum width could be wider than the constant SCALED_MAX_ITEM_TEXT_WIDTH
  // in mobile card list view so far
  const widthWithoutDateString = Math.max(
    titleMetrics.width,
    settings.showNarrative ? narrativeTextSize.width : 0,
    identifiersLineWidth,
    settings.showSummary ? summaryMetrics.width : 0,
    settings.showImage && item.imagePath ? SCALED_IMAGE_WIDTH : 0
  );

  // Find the suitable size of the date line
  const { width: dateLineStrWidth, height: dateLineHeight } = calcItemCardDateLineRelated(
    settings.showDates,
    settings.showDuration,
    item.dateTextSize,
    item.durationTextSize,
    fontSize,
    widthMap,
    Math.max(widthWithoutDateString, SCALED_MAX_ITEM_TEXT_WIDTH)
  );

  const width =
    widthWithoutDateString > SCALED_MAX_ITEM_TEXT_WIDTH
      ? widthWithoutDateString
      : Math.max(widthWithoutDateString, dateLineStrWidth);

  const additionalMargin = settings.showImage && item.imagePath ? SCALED_IMAGE_MARGIN_TOP : 0;

  // Height is the sum of the title, date, avatars, summary, and internal padding
  const height =
    titleMetrics.height +
    dateLineHeight +
    (settings.showNarrative ? narrativeTextSize.height : 0) +
    identifiersLineHeight +
    (settings.showSummary ? summaryMetrics.height : 0) +
    (settings.showImage && item.imagePath ? SCALED_IMAGE_HEIGHT : 0) +
    additionalMargin * 2;

  // TODO: Add in other items
  //   - Info Icons
  return immutableRecords.SizeRecord({ width: width + 1, height });
};

const getAvailableContentWidth = (item, maxWidth, fontSize, metricsSuffix, showDragHandles) => {
  const isNarrativeView = metricsSuffix === METRICS_PREFIX.NARRATIVE;
  const superDepth = isNarrativeView ? (item.narrativePosition ? item.narrativePosition.size - 1 : 0) : item.superDepth;
  const hasChildren = isNarrativeView ? item.narrativeHasChildren : item.children && item.children.size > 0;

  return getItemCardNestedTextWidth(maxWidth, fontSize, superDepth, hasChildren, showDragHandles);
};

const calcMobileItemCard = (item, settings, fontSize, widthMap, metricsSuffix, maxWidth, showDragHandles) => {
  const titleMetrics = metricsSuffix ? item.get(`${metricsSuffix}TitleMetrics`).size : item.titleMetrics.size;
  const narrativeTextSize = item.narrativeTextSize.size;
  const summaryMetrics = metricsSuffix ? item.get(`${metricsSuffix}SummaryMetrics`).size : item.summaryMetrics.size;

  const SCALED_IMAGE_HEIGHT = scalePixelsToFontSize(ITEM_CARD_IMAGE_HEIGHT, fontSize);
  const SCALED_MARGIN_Y = scalePixelsToFontSize(ITEM_CARD_MARGIN_Y, fontSize);

  const availableContentWidth = getAvailableContentWidth(item, maxWidth, fontSize, metricsSuffix, showDragHandles);

  const { height: avatarLineHeight } = getRelatedIdentifersLineSize(
    item,
    settings,
    fontSize,
    widthMap,
    availableContentWidth
  );

  const { height: dateLineHeight } = calcItemCardDateLineRelated(
    settings.showDates,
    settings.showDuration,
    item.dateTextSize,
    item.durationTextSize,
    fontSize,
    widthMap,
    availableContentWidth
  );

  const additionalMargin =
    (settings.showSummary && summaryMetrics.height) || (settings.showImage && item.imagePath) ? SCALED_MARGIN_Y : 0;

  // Height is the sum of the title, date, avatars, summary, and internal padding
  const height =
    titleMetrics.height +
    dateLineHeight +
    (settings.showNarrative ? narrativeTextSize.height : 0) +
    avatarLineHeight +
    (settings.showSummary ? summaryMetrics.height : 0) +
    (settings.showImage && item.imagePath ? SCALED_IMAGE_HEIGHT : 0) +
    additionalMargin;

  // TODO: Add in other items
  //   - Info Icons
  return immutableRecords.SizeRecord({ width: availableContentWidth, height });
};

export default {
  calcItemCardSize,
  calcMobileItemCard
};
